import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { useOrgData } from '../hooks/useOrgData';
import { deriveIsSuperAdmin } from '../utils/rbac';
import { OPEN_UPLOAD_EVENT } from '../utils/uploadModal';

const IS_LANDING = process.env.REACT_APP_BUILD_TARGET === 'landing';
const APP_URL = process.env.REACT_APP_APP_URL || 'https://app.retainiq.cloud';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { toasts, showToast, removeToast } = useToast();
  const { user, signOut, isAuthenticated, isLoading, token } = useAuth();
  const isSuperAdmin = deriveIsSuperAdmin(token);
  const { data: interventionsData } = useOrgData<any[]>('interventions');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingSignalCount, setPendingSignalCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  
  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);
  
  // Open the upload modal when any component (e.g. an EmptyState CTA) requests it.
  useEffect(() => {
    const handleOpenUpload = () => setShowUploadModal(true);
    window.addEventListener(OPEN_UPLOAD_EVENT, handleOpenUpload);
    return () => window.removeEventListener(OPEN_UPLOAD_EVENT, handleOpenUpload);
  }, []);

  // Pending-signal badge reflects the org's real interventions; zero when signed out.
  useEffect(() => {
    if (!isAuthenticated) {
      setPendingSignalCount(0);
      return;
    }
    const list = Array.isArray(interventionsData) ? interventionsData : [];
    setPendingSignalCount(list.filter((i: any) => i.status === 'PENDING').length);
  }, [isAuthenticated, interventionsData]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };
  
  const handleUploadSubmit = () => {
    if (!selectedFile) {
      showToast('Please select a file to upload', 'error');
      return;
    }
    
    // Simulate upload
    showToast(`File "${selectedFile.name}" uploaded successfully!`, 'success');
    setShowUploadModal(false);
    setSelectedFile(null);
  };
  
  const handleNotificationClick = (_index: number) => {
    showToast('Notification marked as read', 'info');
    // In a real app, this would mark the notification as read
  };
  
  const handleViewAllNotifications = () => {
    setShowNotifications(false);
    showToast('All notifications viewed', 'info');
    // In a real app, this would navigate to a notifications page
  };
  
  const handleSignOut = async () => {
    try {
      await signOut();
      // Clear all local storage
      localStorage.clear();
      sessionStorage.clear();
      showToast('Signed out successfully', 'success');
      // Force a complete page reload to clear all state
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (error) {
      console.error('Sign out error:', error);
      showToast('Error signing out', 'error');
    }
  };
  
  // Don't render navigation content until auth context is ready
  // This prevents flashing of undefined user data during auth resolution
  if (isLoading) {
    // Return minimal nav structure with skeleton loader to maintain layout
    return (
      <nav className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50 backdrop-blur-sm bg-slate-800/95" role="navigation" aria-label="Main navigation">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <img src="/logo.png" alt="RetainIQ" className="w-32 h-auto object-contain -my-2" />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Skeleton loader for user info */}
              <div className="hidden md:flex items-center space-x-2 px-3 py-2 bg-slate-700/50 rounded-lg" style={{ minWidth: '200px' }}>
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div className="h-4 w-32 bg-slate-600 rounded animate-pulse" data-loading="true"></div>
              </div>
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        </div>
      </nav>
    );
  }
  
  if (IS_LANDING) {
    return (
      <nav className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50 backdrop-blur-sm bg-slate-800/95" role="navigation" aria-label="Main navigation">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center group" aria-label="RetainIQ - go to home">
              <img src="/logo.png" alt="RetainIQ" className="w-32 h-auto object-contain group-hover:opacity-90 transition-opacity -my-2" />
            </Link>
            <div className="flex items-center space-x-1">
              {[
                { to: '/pricing', label: 'Pricing' },
                { to: '/security', label: 'Security' },
                { to: '/demo', label: 'Demo' },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    location.pathname === to
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  {label}
                </Link>
              ))}
              <a
                href={`${APP_URL}/login`}
                className="ml-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Sign In
              </a>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <>
      <nav className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50 backdrop-blur-sm bg-slate-800/95" role="navigation" aria-label="Main navigation">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + Brand */}
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center group" aria-label="RetainIQ - Retain your team's critical knowledge, go to home">
              <img
                src="/logo.png"
                alt="RetainIQ"
                className="w-32 h-auto object-contain group-hover:opacity-90 transition-opacity -my-2"
              />
            </Link>

            {/* Center: Tab Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <Link
                to="/"
                style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive('/')
                    ? 'bg-slate-700 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`}
                aria-current={isActive('/') ? 'page' : undefined}
              >
                Dashboard
              </Link>
              <Link
                to="/graph"
                style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive('/graph')
                    ? 'bg-slate-700 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`}
                aria-current={isActive('/graph') ? 'page' : undefined}
              >
                Knowledge Graph
              </Link>
              <Link
                to="/interventions"
                style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive('/interventions')
                    ? 'bg-slate-700 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`}
                aria-current={isActive('/interventions') ? 'page' : undefined}
              >
                Interventions
              </Link>
              <Link
                to="/settings"
                style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive('/settings')
                    ? 'bg-slate-700 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`}
                aria-current={isActive('/settings') ? 'page' : undefined}
              >
                Settings
              </Link>
              {isSuperAdmin && (
                <Link
                  to="/admin"
                  style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive('/admin')
                      ? 'bg-slate-700 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                  aria-current={isActive('/admin') ? 'page' : undefined}
                >
                  Admin
                </Link>
              )}
            </div>
          </div>
          
          {/* Right: Actions */}
          <div className="flex items-center space-x-4">
            {/* Upload Signal Button - only show when authenticated */}
            {isAuthenticated && (
              <button 
                onClick={() => setShowUploadModal(true)}
                style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all hover:shadow-lg hover:shadow-blue-500/50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>Upload Signal</span>
              </button>
            )}
            
            {/* Notification Bell - only show when authenticated */}
            {isAuthenticated && (
              <div className="relative" ref={notificationRef}>
                <div className="relative group">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                    className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    aria-label={`Notifications${pendingSignalCount > 0 ? `, ${pendingSignalCount} pending` : ''}`}
                    aria-expanded={showNotifications}
                    aria-haspopup="true"
                  >
                    <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {/* Pending signal badge — only shown when count > 0 */}
                    {pendingSignalCount > 0 && (
                      <span
                        role="status"
                        aria-label={`${pendingSignalCount} signal${pendingSignalCount !== 1 ? 's' : ''} pending processing`}
                        className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center"
                      >
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 text-[10px] font-bold text-white items-center justify-center">
                          {pendingSignalCount}
                        </span>
                      </span>
                    )}
                  </button>
                  {/* CSS-only tooltip */}
                  {pendingSignalCount > 0 && (
                    <div className="absolute right-0 top-full mt-1 px-2 py-1 bg-slate-900 text-slate-200 text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      {`${pendingSignalCount} signal${pendingSignalCount !== 1 ? 's' : ''} pending processing`}
                    </div>
                  )}
                </div>
                
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-2xl z-50" style={{ backgroundColor: '#334155' }}>
                    <div className="p-4 border-b border-slate-600" style={{ backgroundColor: '#334155' }}>
                      <h3 className="text-sm font-semibold text-white">Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto" style={{ backgroundColor: '#334155' }}>
                      {[
                        { title: 'New critical risk detected', desc: 'Payment Processing score: 92', time: '2 min ago', type: 'critical' },
                        { title: 'Intervention generated', desc: 'Questions for Raj Kumar', time: '15 min ago', type: 'info' },
                        { title: 'Score updated', desc: 'CI/CD Pipeline: 85 (↑ from 82)', time: '1 hour ago', type: 'warning' },
                        { title: 'New signal processed', desc: 'Meeting transcript analyzed', time: '2 hours ago', type: 'success' },
                      ].map((notif, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleNotificationClick(idx)}
                          className="p-4 border-b border-slate-600 hover:bg-slate-600 cursor-pointer transition-colors"
                          style={{ backgroundColor: '#334155' }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Notification: ${notif.title}. ${notif.desc}. ${notif.time}`}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(idx); } }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              notif.type === 'critical' ? 'bg-red-400' :
                              notif.type === 'warning' ? 'bg-amber-400' :
                              notif.type === 'success' ? 'bg-green-400' : 'bg-blue-400'
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-white">{notif.title}</p>
                              <p className="text-xs text-slate-200 mt-1">{notif.desc}</p>
                              <p className="text-xs text-slate-400 mt-1">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 border-t border-slate-600" style={{ backgroundColor: '#334155' }}>
                      <button 
                        onClick={handleViewAllNotifications}
                        style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }} 
                        className="w-full text-center text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                      >
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* User Info & Auth Action */}
            <>
              {/* User email / "Not signed in · Sign In" */}
              <div
                className="hidden md:flex items-center space-x-2 px-3 py-2 bg-slate-700/50 rounded-lg"
                style={{ minWidth: '200px' }}
                data-testid="nav-auth-state"
              >
                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {isLoading ? (
                  <div className="h-4 w-32 bg-slate-600 rounded animate-pulse" data-loading="true" />
                ) : user ? (
                  <span className="text-sm text-slate-300 truncate">
                    {user.signInDetails?.loginId || user.username || 'User'}
                  </span>
                ) : (
                  <span className="text-sm text-slate-400 flex items-center gap-2">
                    Not signed in
                    <span className="text-slate-400" aria-hidden="true">·</span>
                    <button
                      onClick={() => window.location.href = '/login'}
                      className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                      aria-label="Sign in"
                    >
                      Sign In
                    </button>
                  </span>
                )}
              </div>

              {/* Sign Out — only when authenticated */}
              {isAuthenticated && (
                <button
                  onClick={handleSignOut}
                  style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 hover:text-white rounded-lg text-sm font-medium transition-colors"
                  aria-label="Sign out"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              )}
            </>

            {/* Mobile hamburger — visible only below md breakpoint */}
            <button
              onClick={() => setMobileMenuOpen(prev => !prev)}
              data-testid="hamburger-button"
              className="md:hidden p-2 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-700 transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>
    
    {/* Mobile slide-out drawer */}
    {mobileMenuOpen && (
      <>
      {/* Transparent backdrop — closes drawer when tapped outside */}
      <div
        className="md:hidden fixed inset-0 z-40 bg-black/20"
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />
      <div
        data-testid="mobile-drawer"
        className="md:hidden fixed inset-y-0 right-0 z-50 w-72 bg-slate-800 border-l border-slate-700 shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Mobile navigation menu"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <img src="/logo.png" alt="RetainIQ" className="w-24 h-auto object-contain -my-1" />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-700"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {[
            { to: '/', label: 'Dashboard' },
            { to: '/graph', label: 'Knowledge Graph' },
            { to: '/interventions', label: 'Interventions' },
            { to: '/settings', label: 'Settings' },
            ...(isSuperAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
          ].map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(to)
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="px-4 py-6 border-t border-slate-700 space-y-3">
          {isAuthenticated && (
            <button
              onClick={() => { setMobileMenuOpen(false); setShowUploadModal(true); }}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Upload Signal</span>
            </button>
          )}
          {!isAuthenticated && (
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full block text-center px-4 py-2.5 bg-slate-700 text-slate-200 rounded-lg font-medium hover:bg-slate-600 transition-colors text-sm"
            >
              Sign In
            </Link>
          )}
          {isAuthenticated && (
            <button
              onClick={() => { setMobileMenuOpen(false); handleSignOut(); }}
              className="w-full px-4 py-2.5 bg-slate-700 text-slate-200 rounded-lg font-medium hover:bg-slate-600 transition-colors text-sm"
            >
              Sign Out
            </button>
          )}
        </div>
      </div>
      </>
    )}

    {/* Upload Signal Modal */}
    {showUploadModal && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUploadModal(false)}>
        <div className="bg-slate-800 border border-slate-700 rounded-lg max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-100">Upload Workplace Signal</h2>
            <button onClick={() => setShowUploadModal(false)} style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }} className="text-slate-400 hover:text-white transition-colors" aria-label="Close upload dialog">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="signal-type" className="block text-sm font-medium text-slate-300 mb-2">Signal Type</label>
              <select id="signal-type" className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Signal type">
                <option>Meeting Transcript</option>
                <option>Chat Message</option>
                <option>Task Assignment</option>
                <option>Code Reference</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Upload File</label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-slate-500 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept=".json,.txt,.vtt"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <svg className="w-12 h-12 text-slate-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {selectedFile ? (
                    <p className="text-sm text-green-400 mb-1">Selected: {selectedFile.name}</p>
                  ) : (
                    <p className="text-sm text-slate-300 mb-1">Click to upload or drag and drop</p>
                  )}
                  <p className="text-xs text-slate-400">JSON, TXT, or VTT files</p>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleUploadSubmit} style={{ overflow: 'hidden', WebkitTapHighlightColor: 'transparent' }} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors">
                Upload Signal
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Toast Notifications */}
    <div className="fixed top-20 right-6 z-50 space-y-2">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  </>
  );
};

export default Navigation;
